import { ICoordinates, ILayerDetailsData, IMapData, IMapLayer, IMapLayerData, IMapLayerDetails, MapLayerTypes } from "../../shared/models/map_models";
import { GeoJSONFeature, GeoJSONFeatureCollection, GeoJSONPolygon } from "./map-models";
import { FeatureCollection, Polygon } from 'geojson';

const CountLayerSuffix = '-counter'
const ClusterTextSuffix = '-cluster-text'

export const convertAddressToMapPoint = (
    mapLayer: IMapLayerData, 
): GeoJSONFeatureCollection => {

    const titleConfig = mapLayer.detailed_view_configs?.details?.find((x) => x.type === 'title')

    const features: GeoJSONFeature[] = mapLayer.data.map((data: any, index: number) => {

        let currentLon = data.geocoded_address.location.lon
        let currentLat = data.geocoded_address.location.lat

        if (mapLayer.map_configs.jiggle_overlap){
            const radius = 0.001; // ~10 meters
            const angle = (index / mapLayer.data.length) * 2 * Math.PI;
            currentLon = currentLon + radius * Math.cos(angle)
            currentLat = currentLat + radius * Math.sin(angle)
        }

        return ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [currentLon, currentLat],
            },
            properties: {
                spacecow_title: titleConfig?.key ? data[titleConfig?.key] : "",
                data
            },
        })
    })

    return {
        type: 'FeatureCollection',
        features: features
    }
}


export const convertChoroplethDataToPolygon = (mapData: Array<IMapData>): FeatureCollection<Polygon> => {
    const featuresToAdd: GeoJSONPolygon[] = []
    mapData.forEach((data) => {
        if(data.geojson){
            featuresToAdd.push({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: data.geojson,
                },
                properties: {
                    id: data.country_name,
                    count: data.current_count,
                },
            })
        }
    })

    return {
        type: 'FeatureCollection',
        features: featuresToAdd
    }
}

export const getChoroplethPolygonCenter = (mapData: Array<IMapData>): GeoJSONFeatureCollection => {

    const featuresToAdd: Array<GeoJSONFeature> = []
    mapData.forEach((data) => {
        if(data.center)
            featuresToAdd.push(
                {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: data.center
                    },
                    properties: {
                        id: data.country_name,
                        count: data.current_count
                    }
                }
            )
    })
    return {
        type: 'FeatureCollection',
        features: featuresToAdd
    }
}


export const convertBoundingBox = (bounds: mapboxgl.LngLatBounds): Array<ICoordinates> => {

    const boundingBox: Array<ICoordinates> = []
    boundingBox.push({lat: bounds.getNorthWest().lat, lon: bounds.getNorthWest().lng})
    boundingBox.push({lat: bounds.getNorthEast().lat, lon: bounds.getNorthEast().lng})
    boundingBox.push({lat: bounds.getSouthEast().lat, lon: bounds.getSouthEast().lng})
    boundingBox.push({lat: bounds.getSouthWest().lat, lon: bounds.getSouthWest().lng})

    return boundingBox
}

export const getMapLayerSourceId = (mapLayer: IMapLayer) => {
    return `${mapLayer.name.replace(" ", "-").toLowerCase()}-${mapLayer.id}`
}

export const getMapLayerIdFromSourceId = (layerSourceId: string): number => {
    const splitLayerName = layerSourceId.split("-")
    return Number(splitLayerName[splitLayerName.length - 1])
}

export const getMapConfigsForLayer = (mapLayer: IMapLayer) => {
    const mapSourceId = getMapLayerSourceId(mapLayer)
    const layerMapConfigs = mapLayer.map_configs

    switch(layerMapConfigs.type.toLowerCase()){
        case MapLayerTypes.Point:
            return [
                 {
                    id: mapSourceId,
                    type: 'symbol',
                    source: mapSourceId,
                    layout: {
                        'icon-image': mapSourceId,
                        'icon-size': 1,
                        'text-field': `{spacecow_title}`,
                        'text-font': ['Roboto Black', 'Arial Unicode MS Bold'],
                        'text-offset': [1, 0],
                        'text-anchor': 'top-left',
                        'text-justify': 'left',
                        'text-max-width': 8,
                        'text-allow-overlap': false,
                        'icon-allow-overlap': false
                    },
                    paint: {
                        'text-color': 'white',
                        'icon-color': layerMapConfigs.point_color
                    }
                },
            ]

        case MapLayerTypes.Cluster:
            const circleColorConfigs = ['step', ['get', 'point_count']]
            if (layerMapConfigs.cluster_configs){
                layerMapConfigs.cluster_configs.forEach((config) => {
                    if (config.point_count) circleColorConfigs.push(config.point_count)
                    circleColorConfigs.push(config.color)
                })
            }
            const countLayerSourceId = mapSourceId + CountLayerSuffix
            const clusterConfigs: Array<any> =  [
                {
                    id: mapSourceId,
                    type: 'circle',
                    source: mapSourceId,
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': circleColorConfigs,
                        'circle-radius': [
                            'step',
                            ['get', 'point_count'],
                            20,
                            10,
                            30,
                            50,
                            40,
                            100,
                            50
                        ],
                        'circle-opacity': 0.6
                    }
                },
            ]
            
            clusterConfigs.push(
                {  // Layer for cluster count (the number)
                    id: countLayerSourceId,
                    type: 'symbol',
                    source: mapSourceId,
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': '{point_count_abbreviated}',
                        'text-font': ['Roboto Black', 'Arial Unicode MS Bold'],
                        'text-size': 12,
                        'text-allow-overlap': true,
                        'text-offset': layerMapConfigs.count_text ? [0, -.5] : [0,0],
                    }
                }
            )
            
            if(layerMapConfigs.count_text) {
                const clusterTextId = mapSourceId + ClusterTextSuffix
                clusterConfigs.push(
                    {  // Layer for cluster_text if it exists
                        id: clusterTextId,
                        type: 'symbol',
                        source: mapSourceId,
                        filter: ['has', 'point_count'],
                        layout: {
                            'text-field': layerMapConfigs.count_text,
                            'text-font': ['Roboto Black', 'Arial Unicode MS Bold'],
                            'text-size': 12,
                            'text-allow-overlap': true,
                            'text-offset': [0,.5],
                        }
                    }
                )
            }
            return clusterConfigs
        case MapLayerTypes.Choropleth:
            return [
                {
                    id: `${mapSourceId}-fill`,
                    type: 'fill',
                    source: mapSourceId,
                    paint: {
                        'fill-color': '#fff200',
                        'fill-opacity': 0.8,
                    },
                },
                {
                    id: `${mapSourceId}-outline`,
                    type: 'line',
                    source: mapSourceId,
                    paint: {
                        'line-color': '#000',
                        'line-width': 2,
                    },
                },
                {
                    id: `${mapSourceId}-center`,
                    type: 'symbol',
                    source: `${mapSourceId}-center`,
                    layout: {
                        'text-field': ['get', 'count'],
                        'text-size': 14,
                        'text-anchor': 'center',
                        'text-font': ['Roboto Black', 'Arial Unicode MS Bold'],
                        'text-allow-overlap': true,
                    }, paint: {
                        'text-halo-color': 'white',
                        'text-halo-width': 1
                    }
                }
            ]
        default:
            return null
    }
}

export const getObjectValueFromKey = (key: string, object: any) => {
    let dataValue: any = null
    if(key.includes(".")){
        const splitKey = key.split(".")

        splitKey.forEach((key) => {
            if (!dataValue)
                dataValue = object[key]
            else
                dataValue = dataValue[key]
        })
    } else {
        dataValue = object[key]
    }

    return dataValue
}

export const getDataFromDetailedViewConfigs = (data: any, viewConfigs: IMapLayerDetails): any => {

    var foundValue = ""
    if (viewConfigs.key)
        foundValue = getObjectValueFromKey(viewConfigs.key, data)

    return foundValue
}


export const isAddressEmpty = (address: any): boolean => {
    if (address === null || address === '')
        return true

    if (!address?.line_1 && !address?.line_2 && !address?.city && !address?.state && !address?.zip)
        return true


    if (address?.line_1 === '' && address?.line_2 === '' && address?.city === '' && 
        address?.state === '' && address?.zip === '')
        return true

    return false
}


export const getDisplayDataForKeyValue = (detailData: ILayerDetailsData, mapData: IMapData) => {
    var displayData: any = null
    var keyToGet = detailData.key
    var lastDotIndex = keyToGet.lastIndexOf(".");
    if(lastDotIndex !== -1)
        keyToGet = keyToGet.substring(0, lastDotIndex)

    const objValue = getObjectValueFromKey(keyToGet, mapData)
    if(Array.isArray(objValue)){
        var dataKey = detailData.key.substring(lastDotIndex).replace(".", "")
        if(detailData.display === 'first'){
            if (detailData.filter){
                const filter = detailData.filter
                const matchingValue = objValue.find(x =>{
                    if(filter.method === 'equals')
                        return x[filter?.field as any] === filter.value
                })
                if(matchingValue)
                    displayData = matchingValue[dataKey]
            } else {
                if(objValue.length > 0)
                    displayData = objValue[0]
            }
        } else if (detailData.display === 'list'){
            displayData = ""
            objValue.forEach((x, i) => {
                var valToAdd = x[dataKey]
                if(typeof valToAdd !== 'object' && valToAdd){
                    
                    let skipVal = false
                    if (detailData.filter){
                        const filter = detailData.filter
                        if(filter.method === 'equals'){
                            if (x[filter.field] !== filter.value)
                                skipVal = true
                        }
                    }
                    if(!skipVal){
                        displayData += `${valToAdd}`
                        if (i < objValue.length - 1){
                            displayData += ", "
                        }
                    }
                }
            })
            displayData = displayData.trim()
            if (displayData.endsWith(","))
                displayData = displayData.slice(0, -1)
            
        } else if (detailData.display === 'count'){
            const dataLength = objValue.length
            displayData = dataLength.toString()

            const filter = detailData.filter
            if(filter){
                if(filter.method === 'greater-than'){
                    if(dataLength <= filter.value)
                        displayData = ''
                }
            }
        } 
        else if (detailData.type === 'object_list') {
            let objectsToDisplay: any = []
             objValue.forEach((x, i) => {
                let skipVal = false
                if (detailData.filter){
                    const filter = detailData.filter
                    if(filter.method === 'equals'){
                        if (x[filter.field] !== filter.value){
                            skipVal = true
                        }
                    }
                }
                
                if(!skipVal){
                    objectsToDisplay.push(x)
                }
            })

            return objectsToDisplay
            
        } else {
            console.error("Unhandled display type: ", detailData)
        }

    } else {
        displayData = objValue || ''
        const filter = detailData.filter
        if(filter){
            if(filter.method === 'greater-than'){
                if(displayData <= filter.value)
                    displayData = ''
            }
        }
    }

    if (detailData?.fields !== undefined && detailData.fields !== null && 
        typeof displayData === 'object' && displayData !== null){
        var newDisplayData = ''
        detailData.fields.forEach((field) => {
            newDisplayData += `${displayData[field] || ''} `
        })

        displayData = newDisplayData.trim()
    }

    if (detailData?.suffix !== null && detailData?.suffix !== undefined && displayData !== null && displayData !== ''){
        displayData += ` ${detailData.suffix}`
    }

    if(detailData?.prefix !== null && detailData?.prefix !== undefined && displayData !== null && displayData !== ''){
        displayData = detailData.prefix + displayData
    }

    return displayData

}